
import { debugMode, USE_FUSE_ONLY } from './filter-logic-config.js';

/**
 * Apply fuzzy reranking to candidates. Detects two-pass results via _twoPassSearch marker
 * and splits protected (HIGH PRIORITY) from fuzzy (LOW PRIORITY) candidates.
 * @returns {{ output: Array, fuseWasUsed: boolean }}
 */
export function applyFuzzyRerank({ candidates, query, filterManager }) {
    try {
        const hasTwoPassResults = candidates.some(item => item._twoPassSearch === true);
        if (hasTwoPassResults) {
            return applyTwoPassRerank(candidates, query, filterManager);
        }
        return applySinglePassRerank(candidates, query, filterManager);
    } catch (e) {
        console.warn('[FilterManager] Fuzzy rerank failed:', e);
        return { output: candidates, fuseWasUsed: false };
    }
}

function getFuzzyApi() {
    return USE_FUSE_ONLY
        ? (window.FuseSearch || window.fuseSearch)
        : (window.fuzzySearch || window.FuzzySearch);
}

function getRankMethodName(fuzzyApi) {
    return USE_FUSE_ONLY
        ? (fuzzyApi && typeof fuzzyApi.rankItems === 'function' ? 'rankItems' : null)
        : (fuzzyApi && typeof fuzzyApi.rankItemsFuse === 'function' ? 'rankItemsFuse' : null);
}

function attachFullPath(items) {
    return items.map(item => {
        const path = item.p || item.path || '';
        const name = item.n || item.name || '';
        const normPath = String(path).replace(/\\/g, '/');
        const fullPath = normPath ? `/${normPath}/${name}` : name;
        return { ...item, fullPath };
    });
}

function attachFuseMatches(ranked, query) {
    return ranked.map(r => {
        const item = { ...r.item };
        if (r.matches) {
            item._fuseMatches = r.matches;
            item._fuseQuery = query;
        }
        return item;
    });
}

function rankItems(items, query, fuzzyApi, rankMethod) {
    const withFullPath = attachFullPath(items);
    return fuzzyApi[rankMethod](query, withFullPath, {
        keys: ['fullPath', 'name', 'path', 'n', 'p'],
        threshold: 0.3,
        includeMatches: true
    });
}

function sortBySecondaryScore(ranked) {
    ranked.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const scoreA = a.item.score || 0;
        const scoreB = b.item.score || 0;
        if (scoreB !== scoreA) return scoreB - scoreA;
        const nameA = (a.item?.n || a.item?.name || '').toLowerCase();
        const nameB = (b.item?.n || b.item?.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
    });
}

function sortByPrimaryScore(ranked) {
    ranked.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const nameA = (a.item?.n || a.item?.name || '').toLowerCase();
        const nameB = (b.item?.n || b.item?.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
    });
}

function engineLabel() {
    return USE_FUSE_ONLY ? 'Fuse.js' : 'uFuzzy+Fuse.js';
}

function splitTwoPassCandidates(candidates) {
    const protectedResults = [];
    const fuzzyCandidates = [];
    for (const item of candidates) {
        const isProtected = item._hasExactPhrase === true || (item.score || 0) > 1;
        if (isProtected) {
            protectedResults.push(item);
        } else {
            fuzzyCandidates.push(item);
        }
    }
    return { protectedResults, fuzzyCandidates };
}

function logTwoPassSplit(candidates, protectedResults, fuzzyCandidates) {
    console.log('[FilterManager] Two-pass results split:', {
        protectedCount: protectedResults.length,
        fuzzyCandidatesCount: fuzzyCandidates.length,
        totalCandidates: candidates.length,
        sampleProtected: protectedResults.slice(0, 3).map(item => ({
            name: item.n || item.name,
            score: item.score,
            hasExactPhrase: item._hasExactPhrase
        })),
        sampleFuzzy: fuzzyCandidates.slice(0, 3).map(item => ({
            name: item.n || item.name,
            score: item.score,
            hasExactPhrase: item._hasExactPhrase
        }))
    });
}

function logBeforeRank(label, query, items) {
    console.log(`[FilterManager] Applying ${engineLabel()} sort to ${label}:`, {
        query,
        candidatesCount: items.length,
        sampleBefore: items.slice(0, 5).map(item => ({
            name: item.n || item.name,
            path: item.p || item.path,
            score: item.score
        }))
    });
}

function logProtectedAfterRank(sortedCount, ranked) {
    console.log(`[FilterManager] ${engineLabel()} sort completed (protectedResults):`, {
        sortedCount,
        top10: ranked.slice(0, 10).map((r, idx) => ({
            position: idx + 1,
            name: r.item?.n || r.item?.name,
            fuseScore: r.score,
            originalScore: r.item.score,
            fullPath: r.item?.fullPath,
            has531211: (r.item?.n || r.item?.name || '').includes('531211')
        }))
    });
}

function logFuzzyAfterRank(sortedCount, ranked) {
    console.log(`[FilterManager] ${engineLabel()} sort completed (fuzzyCandidates):`, {
        sortedCount,
        top10: ranked.slice(0, 10).map(r => ({
            name: r.item?.n || r.item?.name,
            fuseScore: r.score,
            originalScore: r.item.score,
            fullPath: r.item?.fullPath
        }))
    });
}

function applyTwoPassRerank(candidates, query, filterManager) {
    const { protectedResults, fuzzyCandidates } = splitTwoPassCandidates(candidates);

    if (debugMode === '1') {
        logTwoPassSplit(candidates, protectedResults, fuzzyCandidates);
    }

    const fuzzyApi = getFuzzyApi();
    const rankMethod = getRankMethodName(fuzzyApi);

    let sortedProtectedResults = protectedResults;
    let sortedFuzzyResults = fuzzyCandidates;
    let fuseWasUsed = false;

    if (rankMethod && query && !filterManager.isRegexSearch()) {
        fuseWasUsed = true;

        if (protectedResults.length > 0) {
            if (debugMode === '1') logBeforeRank('protectedResults (HIGH PRIORITY)', query, protectedResults);
            const ranked = rankItems(protectedResults, query, fuzzyApi, rankMethod);
            sortBySecondaryScore(ranked);
            sortedProtectedResults = attachFuseMatches(ranked, query);
            if (debugMode === '1') logProtectedAfterRank(sortedProtectedResults.length, ranked);
        }

        if (fuzzyCandidates.length > 0) {
            if (debugMode === '1') logBeforeRank('fuzzyCandidates (LOW PRIORITY)', query, fuzzyCandidates);
            const ranked = rankItems(fuzzyCandidates, query, fuzzyApi, rankMethod);
            sortBySecondaryScore(ranked);
            sortedFuzzyResults = attachFuseMatches(ranked, query);
            if (debugMode === '1') logFuzzyAfterRank(sortedFuzzyResults.length, ranked);
        } else if (debugMode === '1') {
            console.log(`[FilterManager] ${engineLabel()} sort skipped (fuzzyCandidates empty):`, {
                hasFuzzyApi: !!fuzzyApi,
                hasRankMethod: !!rankMethod,
                engine: engineLabel(),
                hasQuery: !!query,
                isRegexSearch: filterManager.isRegexSearch(),
                candidatesCount: fuzzyCandidates.length
            });
        }
    } else if (debugMode === '1') {
        console.log(`[FilterManager] ${engineLabel()} sort skipped (API not available):`, {
            hasFuzzyApi: !!fuzzyApi,
            hasRankMethod: !!rankMethod,
            engine: engineLabel(),
            hasQuery: !!query,
            isRegexSearch: filterManager.isRegexSearch()
        });
    }

    return { output: [...sortedProtectedResults, ...sortedFuzzyResults], fuseWasUsed };
}

function applySinglePassRerank(candidates, query, filterManager) {
    const fuzzyApi = getFuzzyApi();
    const rankMethod = getRankMethodName(fuzzyApi);

    if (rankMethod && query && !filterManager.isRegexSearch() && candidates.length > 0) {
        if (debugMode === '1') logBeforeRank('(single-pass)', query, candidates);

        const ranked = rankItems(candidates, query, fuzzyApi, rankMethod);
        sortByPrimaryScore(ranked);
        const output = attachFuseMatches(ranked, query);

        if (debugMode === '1') {
            console.log(`[FilterManager] ${engineLabel()} sort completed (single-pass):`, {
                sortedCount: output.length,
                top10: ranked.slice(0, 10).map(r => ({
                    name: r.item?.n || r.item?.name,
                    score: r.score,
                    fullPath: r.item?.fullPath
                }))
            });
        }
        return { output, fuseWasUsed: true };
    }

    if (debugMode === '1') {
        console.log(`[FilterManager] ${engineLabel()} sort skipped (single-pass):`, {
            hasFuzzyApi: !!fuzzyApi,
            hasRankMethod: !!rankMethod,
            engine: engineLabel(),
            hasQuery: !!query,
            isRegexSearch: filterManager.isRegexSearch(),
            candidatesCount: candidates.length
        });
    }
    return { output: candidates, fuseWasUsed: false };
}
